import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useCopilotSession, type CopilotSession } from './hooks/useCopilotSession';

/**
 * #1178 (spec §2) — one Copilot session per canvas. The feed and the media cards
 * read the same per-file rows, so a file whose read failed is marked on its card
 * ("Partial run failures show on the file") without a second subscription or replay.
 */
export const CopilotSessionContext = createContext<CopilotSession | null>(null);

export function CopilotSessionProvider({
  tenantId,
  boatId,
  children,
}: {
  tenantId: string | null;
  boatId: string | null;
  children: ReactNode;
}) {
  const session = useCopilotSession(tenantId, boatId);
  return <CopilotSessionContext.Provider value={session}>{children}</CopilotSessionContext.Provider>;
}

/** The canvas's shared session inside the provider; otherwise this component's own. */
export function useSharedCopilotSession(tenantId: string | null, boatId: string | null): CopilotSession {
  const shared = useContext(CopilotSessionContext);
  const own = useCopilotSession(shared ? null : tenantId, shared ? null : boatId);
  return shared ?? own;
}

/** Media ids (as strings) whose read failed in the current run. Empty outside the canvas. */
export function useFailedReadMediaIds(): ReadonlySet<string> {
  const rows = useContext(CopilotSessionContext)?.rows;
  return useMemo(
    () => new Set((rows ?? []).filter((row) => row.state === 'failed').map((row) => String(row.mediaId))),
    [rows],
  );
}
